import React, {
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from "react";
import {
  Row,
  Col,
  Card,
  Select,
  Spin,
  Typography,
  Statistic,
  Table,
  List,
  Tag,
  Button,
  Space,
  Badge,
  Grid,
} from "antd";
import {
  AreaChart,
  Area,
  LineChart,
  Line,
  BarChart,
  Bar,
  PieChart,
  Pie,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as RechartsTooltip,
  Legend,
  ResponsiveContainer,
} from "recharts";
import {
  AuditOutlined,
  UserOutlined,
  ThunderboltOutlined,
  WarningOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
  ReloadOutlined,
  FilterOutlined,
} from "@ant-design/icons";
import axios from "axios";
import dayjs from "dayjs";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import useAuditScope from "./useAuditScope";
import {
  createAuditRequestGate,
  isAuditRequestCanceled,
} from "./auditRequestGate";

const { Title, Text } = Typography;
const { Option } = Select;
const { useBreakpoint } = Grid;

// Couleurs pour les graphiques
const COLORS = {
  success: "#52c41a",
  failure: "#f5222d",
  partial: "#faad14",
  primary: "#1890ff",
  purple: "#722ed1",
  cyan: "#13c2c2",
  orange: "#fa8c16",
  magenta: "#eb2f96",
};

const MODULE_COLORS = [
  "#1890ff",
  "#52c41a",
  "#faad14",
  "#f5222d",
  "#722ed1",
  "#13c2c2",
  "#fa8c16",
  "#eb2f96",
  "#2f54eb",
  "#a0d911",
];

const AuditDashboard = () => {
  const { token } = useAuth();
  const {
    auditScope,
    companyId,
    isSuperAdmin,
    scope: effectiveAuditScope,
    scopeKey: auditScopeKey,
    setAuditScope,
  } = useAuditScope();
  const screens = useBreakpoint();
  const isMobile = !screens.md;
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [dashboardData, setDashboardData] = useState(null);
  const [topModules, setTopModules] = useState([]);
  const [topUsers, setTopUsers] = useState([]);
  const [timeline, setTimeline] = useState([]);
  const [criticalActions, setCriticalActions] = useState([]);
  const [period, setPeriod] = useState(7); // 7 jours par défaut
  const [dateRange, setDateRange] = useState(null);
  const latestAuditScopeKeyRef = useRef(auditScopeKey);
  useLayoutEffect(() => {
    latestAuditScopeKeyRef.current = auditScopeKey;
  }, [auditScopeKey]);
  const previousAuditScopeKeyRef = useRef(auditScopeKey);
  const requestGateRef = useRef(null);
  if (!requestGateRef.current) {
    requestGateRef.current = createAuditRequestGate();
  }

  // Fonction pour charger les données du dashboard
  const loadDashboardData = useCallback(async () => {
    const request = requestGateRef.current.start(auditScopeKey);
    setLoading(true);
    try {
      const params = new URLSearchParams({ period: period.toString() });
      if (effectiveAuditScope === "all") {
        params.append("scope", "all");
      }

      const response = await axios.get(`/api/audit/dashboard?${params}`, {
        headers: {
          Authorization: `Bearer ${token}`,
          ...(companyId ? { "X-Company-Id": String(companyId) } : {}),
        },
        signal: request.signal,
      });

      if (
        request.isCurrent(latestAuditScopeKeyRef.current) &&
        response.data.success
      ) {
        const data = response.data.data;
        setDashboardData(data);
        setTopModules(data.top_modules || []);
        setTopUsers(data.top_users || []);
        setTimeline(data.activity_timeline || []);
        setCriticalActions(data.critical_actions || []);
      }
    } catch (error) {
      if (
        isAuditRequestCanceled(error, request.signal) ||
        !request.isCurrent(latestAuditScopeKeyRef.current)
      ) {
        return;
      }
      console.error("[AUDIT_DASHBOARD] Erreur chargement:", error);
    } finally {
      const isCurrent = request.isCurrent(latestAuditScopeKeyRef.current);
      request.finish();
      if (isCurrent) setLoading(false);
    }
  }, [auditScopeKey, companyId, effectiveAuditScope, period, token]);

  useEffect(() => {
    const scopeChanged = previousAuditScopeKeyRef.current !== auditScopeKey;
    previousAuditScopeKeyRef.current = auditScopeKey;
    if (scopeChanged) {
      requestGateRef.current.cancel();
      setDashboardData(null);
      setTopModules([]);
      setTopUsers([]);
      setTimeline([]);
      setCriticalActions([]);
    }
    loadDashboardData();
  }, [auditScopeKey, loadDashboardData]);

  useEffect(
    () => () => {
      requestGateRef.current?.cancel();
    },
    []
  );

  // Calculer les statistiques globales
  const globalStats = dashboardData?.global_stats || {};
  const totalLogs = globalStats.total_logs || 0;
  const activeUsers = globalStats.active_users || 0;
  const modulesUsed = globalStats.modules_used || 0;
  const successCount = globalStats.success_count || 0;
  const failureCount = globalStats.failure_count || 0;
  const successRate =
    totalLogs > 0 ? ((successCount / totalLogs) * 100).toFixed(1) : 0;

  // Préparer les données pour le graphique de répartition par statut
  const statusData = [
    { name: "Succès", value: successCount, color: COLORS.success },
    { name: "Échecs", value: failureCount, color: COLORS.failure },
  ];

  // Préparer les données pour le graphique des modules
  const moduleChartData = topModules.map((mod) => ({
    module: mod.module,
    actions: mod.total_actions,
    success_rate: parseFloat(mod.success_rate),
  }));

  // Colonnes du tableau des actions critiques
  const criticalActionsColumns = [
    {
      title: "Date",
      dataIndex: "created_at",
      key: "created_at",
      width: 150,
      render: (text) => dayjs(text).format("DD/MM/YYYY HH:mm"),
    },
    {
      title: "Utilisateur",
      dataIndex: "username",
      key: "username",
      width: 160,
      render: (text, record) => {
        if (text) {
          return <Text strong>{text}</Text>;
        }
        if (record.user_id) {
          return <Text type="secondary">ID: {record.user_id}</Text>;
        }
        return <Tag color="default">Système</Tag>;
      },
    },
    {
      title: "Module",
      dataIndex: "module",
      key: "module",
      width: 120,
      render: (text) => <Tag color="blue">{text}</Tag>,
    },
    {
      title: "Action",
      dataIndex: "action_type",
      key: "action_type",
      width: 120,
      render: (text, record) => (
        <Tag color={record.status === "failure" ? "red" : "orange"}>
          {text}
        </Tag>
      ),
    },
    {
      title: "Statut",
      dataIndex: "status",
      key: "status",
      width: 100,
      render: (text) => (
        <Tag
          icon={
            text === "success" ? (
              <CheckCircleOutlined />
            ) : (
              <CloseCircleOutlined />
            )
          }
          color={text === "success" ? "success" : "error"}
        >
          {text === "success" ? "Succès" : "Échec"}
        </Tag>
      ),
    },
    {
      title: "Ressource",
      dataIndex: "resource_type",
      key: "resource_type",
      width: 150,
      render: (text, record) =>
        text ? `${text} #${record.resource_id || "N/A"}` : "N/A",
    },
  ];

  if (loading) {
    return (
      <div style={{ padding: isMobile ? "16px" : "24px", textAlign: "center" }}>
        <Spin size="large" tip="Chargement du dashboard audit..." />
      </div>
    );
  }

  return (
    <div style={{
      padding: isMobile ? "14px" : "24px",
      background: isMobile
        ? "linear-gradient(180deg, rgba(244, 248, 252, 0.95) 0%, rgba(255, 255, 255, 1) 18%)"
        : undefined,
    }}>
      {/* En-tête */}
      <Row
        justify="space-between"
        align={isMobile ? "top" : "middle"}
        gutter={[12, 12]}
        style={{ marginBottom: isMobile ? 16 : 24 }}
      >
        <Col>
          <Title level={isMobile ? 3 : 2} style={{ marginBottom: 4 }}>
            <AuditOutlined /> Dashboard Audit Log
          </Title>
          <Text type="secondary">
            Analyse des activités sur les {period} derniers jours
          </Text>
        </Col>
        <Col xs={24} sm="auto">
          <Space
            direction={isMobile ? "vertical" : "horizontal"}
            style={isMobile ? { width: "100%" } : undefined}
          >
            {isSuperAdmin ? (
              <Select
                value={auditScope}
                onChange={(value) => setAuditScope(value || "company")}
                style={isMobile ? { width: "100%" } : { width: 210 }}
              >
                <Option value="company">Entreprise sélectionnée</Option>
                <Option value="all">Toutes les entreprises</Option>
              </Select>
            ) : null}
            <Select
              value={period}
              onChange={setPeriod}
              style={isMobile ? { width: "100%" } : { width: 150 }}
            >
              <Option value={1}>Aujourd'hui</Option>
              <Option value={7}>7 jours</Option>
              <Option value={30}>30 jours</Option>
              <Option value={90}>90 jours</Option>
            </Select>
            <Button
              icon={<ReloadOutlined />}
              onClick={loadDashboardData}
              block={isMobile}
            >
              Actualiser
            </Button>
          </Space>
        </Col>
      </Row>

      {/* Statistiques globales */}
      <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
        <Col xs={24} sm={12} md={6}>
          <Card>
            <Statistic
              title="Total Actions"
              value={totalLogs}
              prefix={<AuditOutlined />}
              valueStyle={{ color: COLORS.primary }}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} md={6}>
          <Card>
            <Statistic
              title="Utilisateurs Actifs"
              value={activeUsers}
              prefix={<UserOutlined />}
              valueStyle={{ color: COLORS.purple }}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} md={6}>
          <Card>
            <Statistic
              title="Modules Utilisés"
              value={modulesUsed}
              prefix={<ThunderboltOutlined />}
              valueStyle={{ color: COLORS.cyan }}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} md={6}>
          <Card>
            <Statistic
              title="Taux de Succès"
              value={successRate}
              suffix="%"
              prefix={<CheckCircleOutlined />}
              valueStyle={{
                color: successRate >= 95 ? COLORS.success : COLORS.orange,
              }}
            />
          </Card>
        </Col>
      </Row>

      {/* Graphiques principaux */}
      <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
        {/* Timeline d'activité */}
        <Col xs={24} lg={16}>
          <Card title="Timeline d'Activité" bordered={false}>
            <ResponsiveContainer width="100%" height={300}>
              <AreaChart data={timeline}>
                <defs>
                  <linearGradient id="colorActions" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor={COLORS.primary} stopOpacity={0.8} />
                    <stop offset="95%" stopColor={COLORS.primary} stopOpacity={0} />
                  </linearGradient>
                  <linearGradient id="colorSuccess" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor={COLORS.success} stopOpacity={0.8} />
                    <stop offset="95%" stopColor={COLORS.success} stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis
                  dataKey="period"
                  tick={{ fontSize: 12 }}
                  tickFormatter={(value) => {
                    const date = dayjs(value);
                    return date.isValid() ? date.format("DD/MM") : value;
                  }}
                />
                <YAxis tick={{ fontSize: 12 }} />
                <RechartsTooltip />
                <Legend />
                <Area
                  type="monotone"
                  dataKey="total_actions"
                  stroke={COLORS.primary}
                  fillOpacity={1}
                  fill="url(#colorActions)"
                  name="Total Actions"
                />
                <Area
                  type="monotone"
                  dataKey="success_count"
                  stroke={COLORS.success}
                  fillOpacity={1}
                  fill="url(#colorSuccess)"
                  name="Succès"
                />
              </AreaChart>
            </ResponsiveContainer>
          </Card>
        </Col>

        {/* Répartition par statut */}
        <Col xs={24} lg={8}>
          <Card title="Répartition par Statut" bordered={false}>
            <ResponsiveContainer width="100%" height={300}>
              <PieChart>
                <Pie
                  data={statusData}
                  cx="50%"
                  cy="50%"
                  labelLine={false}
                  label={isMobile ? false : (entry) => `${entry.name}: ${entry.value}`}
                  outerRadius={isMobile ? 66 : 80}
                  fill="#8884d8"
                  dataKey="value"
                >
                  {statusData.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={entry.color} />
                  ))}
                </Pie>
                <RechartsTooltip />
              </PieChart>
            </ResponsiveContainer>
          </Card>
        </Col>
      </Row>

      {/* Top modules et utilisateurs */}
      <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
        {/* Top Modules */}
        <Col xs={24} lg={12}>
          <Card title="Top Modules Utilisés" bordered={false}>
            <ResponsiveContainer width="100%" height={isMobile ? 260 : 300}>
              <BarChart data={moduleChartData}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="module" tick={{ fontSize: 11 }} />
                <YAxis tick={{ fontSize: 12 }} />
                <RechartsTooltip />
                <Bar dataKey="actions" fill={COLORS.primary} name="Actions" />
              </BarChart>
            </ResponsiveContainer>
          </Card>
        </Col>

        {/* Top Utilisateurs */}
        <Col xs={24} lg={12}>
          <Card title="Utilisateurs les Plus Actifs" bordered={false}>
            {isMobile ? (
              <List
                dataSource={topUsers}
                locale={{ emptyText: "Aucune activite utilisateur" }}
                renderItem={(item) => (
                  <List.Item key={item.user_id || item.username}>
                    <Space direction="vertical" size={2} style={{ width: "100%" }}>
                      <Text strong>{item.username || "Utilisateur inconnu"}</Text>
                      <Space>
                        <Tag color="blue">{item.total_actions || 0} actions</Tag>
                        <Tag color={item.avg_success_rate >= 95 ? "success" : "warning"}>
                          {item.avg_success_rate || 0}% succes
                        </Tag>
                      </Space>
                    </Space>
                  </List.Item>
                )}
              />
            ) : (
              <Table
                dataSource={topUsers}
                columns={[
                  {
                    title: "Utilisateur",
                    dataIndex: "username",
                    key: "username",
                    render: (text) => <Text strong>{text}</Text>,
                  },
                  {
                    title: "Actions",
                    dataIndex: "total_actions",
                    key: "total_actions",
                    align: "center",
                    render: (val) => <Badge count={val} showZero color={COLORS.primary} />,
                  },
                  {
                    title: "Taux Succès",
                    dataIndex: "avg_success_rate",
                    key: "avg_success_rate",
                    align: "center",
                    render: (val) => (
                      <Tag color={val >= 95 ? "success" : "warning"}>
                        {val}%
                      </Tag>
                    ),
                  },
                ]}
                pagination={false}
                size="small"
                scroll={{ y: 240 }}
              />
            )}
          </Card>
        </Col>
      </Row>

      {/* Actions critiques */}
      <Row gutter={[16, 16]}>
        <Col xs={24}>
          <Card
            title={
              <Space>
                <WarningOutlined style={{ color: COLORS.orange }} />
                <span>Actions Critiques Récentes</span>
              </Space>
            }
            bordered={false}
            extra={
              <Button
                type="link"
                icon={<FilterOutlined />}
                onClick={() => navigate("/audit/logs")}
                block={isMobile}
              >
                Voir tous les logs
              </Button>
            }
          >
            {isMobile ? (
              <List
                dataSource={criticalActions.slice(0, 10)}
                locale={{ emptyText: "Aucune action critique recente" }}
                renderItem={(item) => (
                  <List.Item key={item.id}>
                    <Space direction="vertical" size={2} style={{ width: "100%" }}>
                      <Text strong>{dayjs(item.created_at).format("DD/MM HH:mm")}</Text>
                      <Space wrap size={[6, 6]}>
                        <Tag color="blue">{item.module || "-"}</Tag>
                        <Tag color={item.status === "failure" ? "red" : "orange"}>
                          {item.action_type || "-"}
                        </Tag>
                        <Tag color={item.status === "success" ? "success" : "error"}>
                          {item.status === "success" ? "Succes" : "Echec"}
                        </Tag>
                      </Space>
                      <Text type="secondary">
                        {item.username || (item.user_id ? `ID: ${item.user_id}` : "Systeme")}
                      </Text>
                    </Space>
                  </List.Item>
                )}
              />
            ) : (
              <Table
                dataSource={criticalActions}
                columns={criticalActionsColumns}
                rowKey="id"
                pagination={{ pageSize: 10 }}
                size="small"
                scroll={{ x: 800 }}
              />
            )}
          </Card>
        </Col>
      </Row>
    </div>
  );
};

export default AuditDashboard;

